import { useState, useEffect } from 'react';

interface BatteryInfo {
  level: number;
  charging: boolean;
  chargingTime: number;
  dischargingTime: number;
}

interface DeviceStats {
  battery: number;
  ping: number;
  fps: number;
  storage: number;
  charging: boolean;
}

export function useDeviceStats() {
  const [stats, setStats] = useState<DeviceStats>({
    battery: 62,
    ping: 20,
    fps: 60,
    storage: 45,
    charging: false,
  });

  // Get real battery level
  useEffect(() => {
    const getBatteryStatus = async () => {
      try {
        if ('getBattery' in navigator) {
          const battery = await (navigator as any).getBattery();
          const updateBattery = () => {
            setStats(prev => ({
              ...prev,
              battery: Math.round(battery.level * 100),
              charging: battery.charging,
            }));
          };
          
          battery.addEventListener('levelchange', updateBattery);
          battery.addEventListener('chargingchange', updateBattery);
          updateBattery(); // Initial update
          
          return () => {
            battery.removeEventListener('levelchange', updateBattery);
            battery.removeEventListener('chargingchange', updateBattery);
          };
        }
      } catch (error) {
        console.log('Battery API not available');
      }
    };

    getBatteryStatus();
  }, []);

  // Simulate FPS counter (real FPS measurement would require canvas/requestAnimationFrame)
  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();
    let animationFrameId: number;

    const measureFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      const deltaTime = currentTime - lastTime;

      if (deltaTime >= 1000) {
        const fps = Math.round((frameCount * 1000) / deltaTime);
        setStats(prev => ({
          ...prev,
          fps: Math.min(120, fps),
        }));
        frameCount = 0;
        lastTime = currentTime;
      }

      animationFrameId = requestAnimationFrame(measureFPS);
    };

    animationFrameId = requestAnimationFrame(measureFPS);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  // Simulate ping with small variation
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        ...prev,
        ping: Math.max(10, Math.round(20 + (Math.random() - 0.5) * 20)),
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Simulate storage (in real scenario, would use Storage API)
  useEffect(() => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        const percentUsed = Math.round(((estimate.usage || 0) / (estimate.quota || 1)) * 100);
        setStats(prev => ({
          ...prev,
          storage: percentUsed,
        }));
      });
    }
  }, []);

  return stats;
}
